import {
  AddOutlined,
  DeleteOutlined,
  DragIndicator,
} from '@mui/icons-material';
import { Box, Grid, IconButton } from '@mui/material';
import { styled } from '@mui/material';
import { AppNumberInput } from 'components/FormElements';
import GridContainer from 'components/GridContainer';
import { Row } from 'components/Layout';
import { carrySettings } from 'further-ui/validations';
import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { z } from 'zod';
import FormLabel from 'components/FormElements/FormLabel';

type FormValues = z.infer<
  typeof carrySettings.CarriedInterestFeeTemplatesSettings
>;

const Container = styled(Box)(({ theme }) => ({
  width: '100%',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
}));

type Props = {
  templateIndex: number;
  index: number;
  control: Control<FormValues>;
  onAddRate: () => void;
  onRemoveRate: (index: number) => void;
  showThreshold?: boolean;
  allowDelete: boolean;
};

const CarriedInterestRate: React.FC<Props> = ({
  templateIndex,
  index,
  control,
  onAddRate,
  onRemoveRate,
  showThreshold,
  allowDelete,
}) => {
  return (
    <Container>
      <Row fullWidth centered>
        <GridContainer width="100%">
          <Grid size={{ xs: 12, md: 6 }}>
            <Row centered spacing="sm">
              <FormLabel title="Rate" />
              <Controller
                name={`carriedInterestFeeTemplates.${templateIndex}.carriedInterestRates.${index}.rate`}
                control={control}
                render={({
                  fieldState: { error },
                  field: { onChange, ...rest },
                }) => (
                  <AppNumberInput
                    {...rest}
                    decimals={2}
                    onChange={(value: number) => {
                      onChange(value);
                    }}
                    min={0}
                    max={100}
                    error={!!error}
                    helperText={error?.message}
                    percentEndAdornment
                  />
                )}
              />
            </Row>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {showThreshold && (
              <Row centered spacing="sm">
                <FormLabel title="DPI threshold" />
                <Controller
                  name={`carriedInterestFeeTemplates.${templateIndex}.carriedInterestRates.${index}.dpiThreshold`}
                  control={control}
                  render={({
                    fieldState: { error },
                    field: { onChange, value, ...rest },
                  }) => (
                    <AppNumberInput
                      {...rest}
                      value={value ?? undefined}
                      decimals={2}
                      onChange={(value: number) => {
                        onChange(value);
                      }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Row>
            )}
          </Grid>
        </GridContainer>
        <Row centered>
          <IconButton size="small" color="primary" onClick={onAddRate}>
            <AddOutlined />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => onRemoveRate(index)}
            disabled={!allowDelete}
          >
            <DeleteOutlined />
          </IconButton>
          <DragIndicator />
        </Row>
      </Row>
    </Container>
  );
};

export default CarriedInterestRate;
